import { answers as answersApi } from './api.js'
import { toAnswerPayload } from './items/answer.js'
import { isAnswered } from './items/kinds.js'
import { currentBundle, refreshConfig, formatsOf, findItem, renderSignature } from './config.js'

/**
 * The one way an answer reaches the server.
 *
 * Three rules from the contract are enforced here and nowhere else, so no screen
 * can get them wrong:
 *
 * 1. SKIPPED MEANS NO ROW. A blank is never POSTed — that is a 422 by design.
 *    Clearing an answer DELETEs the row. The absence of a row is the record of a
 *    skip: no sentinel, no null row, no flag. A POST that deleted on blank would
 *    turn one rendering bug into silent data loss (backend #14).
 *
 * 2. config_version AND response_format ARE CLAIMS. The server stamps both from
 *    its own loaded config and only compares ours. Either mismatching is a 409
 *    with nothing written.
 *
 * 3. study_day is never sent. Backend derives it.
 */

export const SAVED = 'saved'
export const CLEARED = 'cleared'
export const STALE = 'stale'

const MISMATCH_CODES = new Set(['config_version_mismatch', 'response_format_mismatch'])

/** The server's machine code for a rejection, wherever it put it. */
export function rejectionCode(err) {
  const detail = err?.body?.detail
  return (detail && typeof detail === 'object' && detail.code) || err?.body?.code || null
}

/**
 * The server telling us we are rendering a stale bundle.
 *
 * 409 is NOT sufficient on its own: `beep_expired` is also a 409, and treating
 * it as a version mismatch would refetch the whole config bundle and retry an
 * answer the server has already refused for an entirely different reason. When
 * a code is present it decides; a bare 409 falls back to the old reading.
 */
export function isStaleConfigRejection(err) {
  if (!err) return false
  const code = rejectionCode(err)
  if (code) return MISMATCH_CODES.has(code)
  return err.status === 409
}

/** Answering, opening or submitting a beep past its expiry. Nothing is written. */
export function isBeepExpired(err) {
  return rejectionCode(err) === 'beep_expired'
}

export const TRANSIENT = 'transient'
export const PERMANENT = 'permanent'

/**
 * CLASSIFY BEFORE WORDING (DS-3 §D). Offering "Try again" for a refusal that
 * can never succeed is this project's oldest failure mode and it has cost
 * participants twice: a retry that repeats the same 4xx forever teaches her the
 * app is broken and that her answer is her problem to fix.
 *
 * Transient means the server never got to decide — it fell over, it was busy,
 * or the phone has no signal. Pressing again is genuinely the right advice.
 *
 * Permanent means the server decided and said no. It will say no again.
 * `beep_expired` and a stale `config_version` are handled before this is
 * reached, because both have their own honest copy; anything else 4xx lands
 * here and must not promise a retry.
 *
 * A missing status is a network failure, not a silent success — the fetch never
 * completed. Transient is the correct and the safe reading.
 */
export function classifyFailure(err) {
  const status = err?.status
  if (!status) return TRANSIENT
  if (status >= 500 || status === 408 || status === 429) return TRANSIENT
  return PERMANENT
}

/**
 * Did this item's presentation actually change across the refresh?
 *
 * This is the difference between a safe retry and recording an answer against
 * options the participant never saw. A bundle can be bumped for a change to a
 * completely unrelated instrument, and re-asking the participant in that case
 * would be pointless. But if THIS item's prompt, format or option set moved, the
 * answer we hold belongs to a question that no longer exists, and it must not be
 * written under the new version.
 */
function presentationChanged(item, before, after) {
  if (!before || !after) return true

  const previous = findItem(before, item.item_id) ?? item
  const current = findItem(after, item.item_id)
  if (!current) return true // the item is gone from the bundle entirely

  if (current.prompt !== previous.prompt) return true
  if (current.response_format !== previous.response_format) return true

  return (
    renderSignature(formatsOf(before)[previous.response_format]) !==
    renderSignature(formatsOf(after)[current.response_format])
  )
}

/**
 * Save one item, or clear it if the participant emptied it.
 *
 * @returns {Promise<{status, safety?, bundle?, refreshed?}>}
 *   'saved'   — written. `safety` carries {level, dismissal_required} when the
 *               item is a safety item and a rule matched, else null.
 *   'cleared' — the row is gone, the item reads as unanswered.
 *   'stale'   — config moved under this item. Re-render it from `bundle` and let
 *               the participant answer it again.
 */
export async function saveAnswer({ item, answer, beepId = null }) {
  if (!isAnswered(answer)) {
    await answersApi.remove(item.item_id, beepId)
    return { status: CLEARED }
  }

  const before = currentBundle()
  const payload = toAnswerPayload({
    item,
    answer,
    configVersion: before?.config_version,
    beepId,
  })

  try {
    const result = await answersApi.save(payload)
    return { status: SAVED, safety: result?.safety ?? null }
  } catch (err) {
    if (!isStaleConfigRejection(err)) throw err

    const after = await refreshConfig()

    if (presentationChanged(item, before, after)) {
      return { status: STALE, bundle: after }
    }

    // Same question, newer bundle: retry once under the version now in force.
    const result = await answersApi.save({
      ...payload,
      config_version: after?.config_version ?? null,
    })
    return { status: SAVED, safety: result?.safety ?? null, refreshed: true }
  }
}
